/**
 * Route geometry for the homepage global-flow diagram, ported from canideal.org
 * homepage.js (desktop curves) and mobile-flow.js (phone rails). Routes are
 * measured from the rendered cards so journeys stay aligned at every width.
 * Seller products travel coloured and turn gold (payment) once delivered.
 * Colours are palette tokens (src/styles/cid/palette*.css) resolved at draw time,
 * because SVG presentation attributes and SMIL values cannot use var().
 */

import { readToken } from '@/lib/themeTokens';

const NS = 'http://www.w3.org/2000/svg';
const GOLD = '--cid-flow-gold';
const EMPTY_BALL = '--cid-flow-ball-empty';
const CYCLE = '18s';

export interface FlowCategory {
  name: string;
  /** Palette token name, e.g. `--cid-flow-flower`. */
  color: string;
  routeClass: string;
}

interface Box {
  left: number;
  right: number;
  top: number;
  bottom: number;
  x: number;
  y: number;
}

type Attrs = Record<string, string | number>;

function el(tag: string, attrs: Attrs, parent?: Element): SVGElement {
  const node = document.createElementNS(NS, tag) as SVGElement;
  Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)));
  parent?.appendChild(node);
  return node;
}

/** Share of `full` path length covered by `outbound` (when the ball turns gold). */
function outboundFraction(full: string, outbound: string): number {
  const geometry = document.createElementNS(NS, 'path');
  geometry.setAttribute('d', full);
  const length = geometry.getTotalLength();
  geometry.setAttribute('d', outbound);
  return length ? geometry.getTotalLength() / length : 1;
}

function ball(
  parent: Element,
  full: string,
  outbound: string,
  category: FlowCategory,
  index: number,
  buyer: boolean,
  mobile: boolean
) {
  const fraction = outboundFraction(full, outbound).toFixed(6);
  const begin = `-${index * 4.5 + (buyer ? 2.25 : 0)}s`;
  const color = readToken(category.color);
  const gold = readToken(GOLD);
  const start = buyer ? readToken(EMPTY_BALL) : color;
  const after = buyer ? color : gold;
  const mark = el(
    'circle',
    {
      class: mobile ? 'cid-mobile-ball' : `moving ${buyer ? 'buyer-cycle' : 'seller-cycle'}`,
      r: mobile ? (buyer ? 3 : 3.4) : buyer ? 3.6 : 4.5,
      fill: start,
      ...(mobile ? { color } : {}),
      'data-category': category.name,
    },
    parent
  );
  if (buyer) {
    mark.setAttribute('stroke', color);
    mark.setAttribute('stroke-width', mobile ? '1.4' : '1.5');
  }
  const timing = { dur: CYCLE, begin, repeatCount: 'indefinite' };
  el('animateMotion', { path: full, ...timing }, mark);
  el(
    'animate',
    {
      attributeName: 'fill',
      values: `${start};${after};${after}`,
      keyTimes: `0;${fraction};1`,
      calcMode: 'discrete',
      ...timing,
    },
    mark
  );
  if (mobile && !buyer) {
    el(
      'animate',
      {
        attributeName: 'color',
        values: `${color};${gold};${gold}`,
        keyTimes: `0;${fraction};1`,
        calcMode: 'discrete',
        ...timing,
      },
      mark
    );
  }
}

function measure(node: Element, origin: DOMRect, scaleX = 1, scaleY = 1): Box {
  const rect = node.getBoundingClientRect();
  return {
    left: (rect.left - origin.left) * scaleX,
    right: (rect.right - origin.left) * scaleX,
    top: (rect.top - origin.top) * scaleY,
    bottom: (rect.bottom - origin.top) * scaleY,
    x: (rect.left - origin.left + rect.width / 2) * scaleX,
    y: (rect.top - origin.top + rect.height / 2) * scaleY,
  };
}

const curve = (from: { x: number; y: number }, to: { x: number; y: number }) => {
  const middle = (from.x + to.x) / 2;
  return ` C${middle} ${from.y} ${middle} ${to.y} ${to.x} ${to.y}`;
};

/** Desktop: curved journeys through each product row, drawn into `routes` (viewBox units). */
export function drawDesktopRoutes(
  root: HTMLElement,
  svg: SVGSVGElement,
  routes: SVGGElement,
  categories: FlowCategory[]
) {
  routes.replaceChildren();
  const origin = svg.getBoundingClientRect();
  if (!origin.width || !origin.height) return;
  const scaleX = svg.viewBox.baseVal.width / origin.width;
  const scaleY = svg.viewBox.baseVal.height / origin.height;
  const box = (selector: string) =>
    measure(root.querySelector(selector) as Element, origin, scaleX, scaleY);
  const supplier = box('.supplier');
  const ai = box('.ai-core');
  const buyer = box('.buyer');
  const trade = box('.trade-support');
  const products = box('.products');

  categories.forEach((category, index) => {
    const product = box(`[data-product="${category.name}"]`);
    const left = { x: products.left, y: product.y };
    const right = { x: products.right, y: product.y };
    // Cross each category horizontally, so emergence matches its visible row.
    const selling = `M${supplier.x} ${supplier.y}${curve(supplier, left)} L${product.x} ${product.y} L${right.x} ${right.y}${curve(right, ai)} L${buyer.x} ${buyer.y}`;
    const sellerCycle = `${selling} L${trade.x} ${trade.y} L${supplier.x} ${supplier.y}`;
    el(
      'path',
      {
        class: `route ${category.routeClass}`,
        style: `stroke:var(${category.color});opacity:.4`,
        d: sellerCycle,
      },
      routes
    );
    ball(routes, sellerCycle, selling, category, index, false, false);

    // Buyer requests keep four parallel, solid outbound lanes.
    const offset = -12 + index * 6;
    const aiLane = { x: ai.x, y: ai.y + offset };
    const buying = `M${buyer.x} ${buyer.y + offset} L${aiLane.x} ${aiLane.y}${curve(aiLane, right)} L${product.x} ${product.y} L${left.x} ${left.y}${curve(left, supplier)}`;
    const buyerCycle = `${buying} L${trade.x} ${trade.y} L${buyer.x} ${buyer.y}`;
    el(
      'path',
      {
        d: buying,
        fill: 'none',
        stroke: readToken(category.color),
        'stroke-width': '1.2',
        opacity: '.5',
      },
      routes
    );
    ball(routes, buyerCycle, buying, category, index, true, false);
  });
}

const RAIL_COLORS = {
  matching: '--cid-flow-rail-matching',
  search: '--cid-flow-rail-search',
  payment: GOLD,
} as const;
type RailKind = keyof typeof RAIL_COLORS;

/** Defs (arrow markers) for the phone layout; rendered once by the component. */
export const MOBILE_MARKERS = Object.entries(RAIL_COLORS).map(([kind, token]) => ({
  id: `cid-mobile-arrow-${kind}`,
  token,
}));

/** Phone: vertical two-way rails between stacked cards plus outer fulfilment/settlement loops. */
export function drawMobileRoutes(
  root: HTMLElement,
  stage: HTMLElement,
  svg: SVGSVGElement,
  layer: SVGGElement,
  categories: FlowCategory[]
) {
  layer.replaceChildren();
  if (!stage.clientWidth) return;
  const origin = stage.getBoundingClientRect();
  const box = (selector: string) => measure(root.querySelector(selector) as Element, origin);
  const supplier = box('.supplier');
  const products = box('.products');
  const ai = box('.ai-core');
  const buyer = box('.buyer');
  const trade = box('.trade-support');
  const down = supplier.x + 7;
  const up = supplier.x - 7;
  const left = supplier.left - 23;
  const right = supplier.right + 23;
  svg.setAttribute('viewBox', `0 0 ${stage.clientWidth} ${stage.clientHeight}`);

  const connection = (d: string, kind: RailKind) =>
    el(
      'path',
      {
        d,
        stroke: readToken(RAIL_COLORS[kind]),
        class: 'cid-mobile-connection',
        'marker-end': `url(#cid-mobile-arrow-${kind})`,
      },
      layer
    );

  // Two matching directions between the same existing cards.
  [supplier, products, ai].forEach((above, index) => {
    const below = [products, ai, buyer][index];
    connection(`M${down} ${above.bottom} V${below.top - 4}`, 'matching');
    connection(`M${up} ${below.top} V${above.bottom + 4}`, 'search');
  });
  connection(`M${down} ${buyer.bottom} V${trade.top - 4}`, 'payment');
  connection(`M${up} ${trade.top} V${buyer.bottom + 4}`, 'matching');

  // Separate outside rails preserve both fulfilment and settlement loops.
  const toTrade = ` H${right - 6} Q${right} ${supplier.y} ${right} ${supplier.y + 6} V${trade.y - 6} Q${right} ${trade.y} ${right - 6} ${trade.y}`;
  const toSupplier = ` H${left + 6} Q${left} ${trade.y} ${left} ${trade.y - 6} V${supplier.y + 6} Q${left} ${supplier.y} ${left + 6} ${supplier.y}`;
  connection(`M${supplier.right} ${supplier.y}${toTrade} H${trade.right + 4}`, 'matching');
  connection(`M${trade.left} ${trade.y}${toSupplier} H${supplier.left - 4}`, 'payment');

  categories.forEach((category, index) => {
    const product = box(`.product[data-product="${category.name}"]`);
    const selling = `M${down} ${supplier.y} V${products.top} L${product.x} ${product.y} L${down} ${products.bottom} V${ai.y} V${buyer.y}`;
    const sellerCycle = `${selling} L${down} ${trade.y} L${trade.left} ${trade.y}${toSupplier} H${down}`;
    const buying = `M${up} ${buyer.y} V${ai.y} V${products.bottom} L${product.x} ${product.y} L${up} ${products.top} V${supplier.y}`;
    const buyerCycle = `${buying} L${supplier.right} ${supplier.y}${toTrade} H${up} L${up} ${buyer.y}`;
    ball(layer, sellerCycle, selling, category, index, false, true);
    ball(layer, buyerCycle, buying, category, index, true, true);
  });
}
